import { useState, useMemo } from 'react';
import {
    ArrowRight,
    ArrowLeft,
    Check,
} from 'lucide-react';
import PatientCapacityStep from './PatientCapacityStep';
import FacilityServicesStep from './FacilityServicesStep';
import MedicalSpecialitiesStep from './MedicalSpecialitiesStep';
import DocumentsStep from './DocumentsStep';
import ReviewStep from './ReviewStep';
import useRegistrationStore from '../../store/useRegistrationStore';
import { createEmpanelment, updateEmpanelment } from '../../api/providerRegistrationApi';
import { getTokenAPI } from '../../helper/helper';
//key should not edit
const baseSteps = [
    {
        num: 1,
        title: 'Patient Capacity',
        key: 'capacity',
        pharmacyTitle: 'Pharmacy Discount',
    },
    { num: 2, title: 'Services', key: 'services' },
    { num: 3, title: 'Specialities', key: 'specialities' },
    { num: 4, title: 'Documents', key: 'documents' },
    { num: 5, title: 'Review', key: 'review' },
];

export default function FinalRegistrationSteps() {
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        formData,
        selectedServices,
        selectedSpecialities,
        updateFormData,
        toggleService,
        toggleSpeciality,
        providerData,
    } = useRegistrationStore();

    const isPharmacy = providerData?.providerType === 2;

    // Filter steps and apply pharmacy-specific labels
    const steps = useMemo(() => {
        let filtered = baseSteps;

        if (isPharmacy) {
            filtered = baseSteps.filter(s => s.key !== 'specialities');
        }

        return filtered.map((s, index) => ({
            ...s,
            displayNum: index + 1,
            // Override title/subtitle if pharmacy
            title: isPharmacy && s.key === 'capacity' ? s.pharmacyTitle : s.title,
        }));
    }, [isPharmacy]);

    const currentStepKey = steps.find(s => s.displayNum === step)?.key || 'capacity';

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            updateFormData({ [name]: files?.[0] || null });
        } else {
            updateFormData({ [name]: value });
        }
    };

    const handleToggleService = (id) => toggleService(id);
    const handleToggleSpeciality = (id) => toggleSpeciality(id);



    const handleFinish = async () => {
        setIsSubmitting(true);
        try {
            const tokenResponse = await getTokenAPI();
            if (tokenResponse.statusCode !== 1 || !tokenResponse.accessToken) {
                throw new Error('Failed to obtain access token');
            }

            const accessToken = tokenResponse.accessToken;
            const store = useRegistrationStore.getState();
            // Check if we're updating an existing empanelment or creating a new one
            const isUpdate = providerData?.empanelmentId && providerData?.empanelmentRefNo;
            let empanelmentResult;

            if (isUpdate) {
                // Update existing empanelment
                empanelmentResult = await updateEmpanelment(
                    store,
                    accessToken,
                    providerData.empanelmentId,
                    providerData.empanelmentRefNo
                );
            } else {
                // Create new empanelment
                empanelmentResult = await createEmpanelment(store, accessToken);
            }

            // Check if the response is valid - access the nested data object
            if (!empanelmentResult?.data || empanelmentResult.data.statusCode !== 1) {
                throw new Error(empanelmentResult?.data?.message || `Failed to ${isUpdate ? 'update' : 'create'} empanelment`);
            }

            // Access details from the nested data object
            const referenceNumber = empanelmentResult.data.details;

            if (!referenceNumber) {
                throw new Error('No reference number received from server');
            }

            store.clearRegistration();

            setTimeout(() => {
                window.location.href = `/#success?ref=${encodeURIComponent(referenceNumber)}`;
            }, 500);
        } catch (err) {
            console.error('Submission error:', err);
            alert(`Failed to submit empanelment. Please try again. Error: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };


    const canProceed = () => {
        // Step 1 (Capacity/Discount): For pharmacy, discount must be > 1
        if (currentStepKey === 'capacity') {
            if (isPharmacy) {
                const discount = parseFloat(formData.discount || 0);
                return discount > -1;
            }
            return true; // For non-pharmacy, always allow proceed
        }

        // Documents step: Check all required files
        if (currentStepKey === 'documents') {
            const requiredFiles = [
                'tradeLicenseFile',
                'haLicenseFile',
                'signatoryEmiratesIdFile',
            ];

            // Add tariffListFile only if NOT pharmacy
            if (!isPharmacy) {
                requiredFiles.push('tariffListFile');
            }

            return requiredFiles.every((key) => {
                const file = formData[key];
                // Only real, live File objects count
                return file instanceof File && file.size > 0;
            });
        }

        // All other steps: allow proceed
        return true;
    };

    const renderStepContent = () => {
        switch (currentStepKey) {
            case 'capacity':
                return <PatientCapacityStep formData={formData} handleInputChange={handleChange} />;
            case 'services':
                return (
                    <FacilityServicesStep
                        formData={formData}
                        handleInputChange={handleChange}
                        selectedServices={selectedServices}
                        toggleService={handleToggleService}
                    />
                );
            case 'specialities':
                return (
                    <MedicalSpecialitiesStep
                        formData={formData}
                        handleInputChange={handleChange}
                        selectedSpecialities={selectedSpecialities}
                        toggleSpeciality={handleToggleSpeciality}
                    />
                );
            case 'documents':
                return <DocumentsStep formData={formData} handleInputChange={handleChange} isPharmacy={isPharmacy} />;
            case 'review':
                return <ReviewStep />;
            default:
                return null;
        }
    };

    const goNext = () => {
        if (step < steps.length) {
            setStep(step + 1);
        }
    };

    const goPrevious = () => {
        if (step > 1) {
            setStep(step - 1);
        } else {
            window.location.href = '/#step-2';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-primary/10 py-8 px-6 relative">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-10">
                    <h1 className="text-4xl font-bold text-gray-900 mb-3">
                        Provider Empanelment
                    </h1>

                </div>

                {/* Progress Steps */}
                <div className="flex justify-center mb-10 overflow-x-auto">
                    <div className="inline-flex gap-3 bg-gray-50 rounded-full shadow-lg p-2 border border-gray-200">
                        {steps.map((s) => (
                            <div
                                key={s.key}
                                className={`
                                    flex items-center gap-3 px-6 py-3 rounded-full transition-all
                                    ${step >= s.displayNum ? 'bg-primary text-white shadow-md' : 'text-gray-500'}
                                `}
                            >
                                <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center font-semibold">
                                    {step > s.displayNum ? <Check size={18} /> : s.displayNum}
                                </div>
                                <div className="hidden sm:block">
                                    <div className="font-medium">{s.title}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Main Form Card */}
                <div className="bg-white rounded-3xl shadow-xl p-6 md:p-10 border border-gray-100 mb-10">
                    {renderStepContent()}
                </div>

                {/* Navigation */}
                <div className="flex justify-between items-center mt-10 max-w-3xl mx-auto">
                    <button
                        onClick={goPrevious}
                        className={`
                            flex items-center gap-3 px-8 py-3.5 rounded-full font-medium transition-all
                            bg-white border-2 border-gray-300 hover:border-primary text-gray-700 hover:text-primary shadow-md
                        `}
                    >
                        <ArrowLeft size={18} />
                        {step === 1 ? 'Back to Authority' : 'Previous Step'}
                    </button>

                    <button
                        onClick={() => {
                            if (step === steps.length) {
                                handleFinish();
                            } else if (canProceed()) {
                                goNext();
                            }
                        }}
                        disabled={!canProceed() || isSubmitting}
                        className={`
                            flex items-center gap-3 px-9 py-3.5 rounded-full font-semibold text-white transition-all shadow-lg
                            ${canProceed() && !isSubmitting
                                ? 'bg-primary hover:bg-primary-dark hover:shadow-xl'
                                : 'bg-gray-400 cursor-not-allowed'}
                        `}
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                                Submitting...
                            </>
                        ) : step === steps.length ? (
                            'Complete Registration'
                        ) : (
                            'Next Step'
                        )}
                        {!isSubmitting && <ArrowRight size={18} />}
                    </button>
                </div>

                {/* Warning */}
                {currentStepKey === 'documents' && !canProceed() && (
                    <p className="text-center text-red-600 mt-4 text-sm font-medium">
                        Please upload all required documents before proceeding
                    </p>
                )}
            </div>

            {/* Loader overlay */}
            {isSubmitting && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4">
                        <svg className="animate-spin h-12 w-12 text-primary" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        <p className="text-lg font-semibold text-gray-800">Submitting your empanelment...</p>
                        <p className="text-sm text-gray-600">Please wait a moment</p>
                    </div>
                </div>
            )}
        </div>
    );
}